import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL, INPOST_GEOWIDGET_TOKEN } from '../config';
import { searchParcelLockers } from '../services/InpostService';
import ConfirmationModal from './ConfirmationModal';
import './OrderFinalization.css';

const DELIVERY_METHODS = {
  INPOST: { name: 'Paczkomaty InPost', price: 13.99 },
};

const PAYMENT_METHODS = [
  { id: 'blik', name: 'BLIK' },
  { id: 'paypal', name: 'PayPal' },
  { id: 'card', name: 'Karta płatnicza' },
];

export default function OrderFinalization() {
  const [cart, setCart] = useState(null);
  const [userData, setUserData] = useState(null);
  const [shippingData, setShippingData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    postalCode: '',
    deliveryMethod: ''
  });
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [parcelLocker, setParcelLocker] = useState('');
  const [parcelLockers, setParcelLockers] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [step, setStep] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalConfirmAction, setModalConfirmAction] = useState(null);
  const [modalCancelAction, setModalCancelAction] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('info');
  const [notificationAutoDismiss, setNotificationAutoDismiss] = useState(false);
  const [useRegisteredAddress, setUseRegisteredAddress] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const quantityUpdateTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const triggerNotificationModal = (message, type, autoDismiss) => {
    setNotificationMessage(message);
    setNotificationType(type);
    setNotificationAutoDismiss(autoDismiss);
    setShowNotificationModal(true);
  };

  useEffect(() => {
    const fetchCart = async () => {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      if (!userId || !token) {
        navigate('/login');
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/api/cart/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch cart');
        }
        const data = await response.json();
        setCart(data);
      } catch (error) {
        setError('Nie udało się załadować koszyka');
      }
    };

    const fetchUserData = async () => {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      if (!userId || !token) {
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }
        const data = await response.json();
        setUserData(data);
        setShippingData({
          firstName: data.name || '',
          lastName: data.surname || '',
          email: data.email || '',
          phone: data.phoneNumber || '',
          street: data.address?.street || '',
          city: data.address?.city || '',
          postalCode: data.address?.postalCode || '',
          deliveryMethod: ''
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchCart();
    fetchUserData();
    setIsLoading(false);
  }, [navigate]);

  const renderParcelLockerSection = () => {
    const handlePointSelect = (event) => {
      const point = event.detail;
      console.log('Selected parcel locker:', point);
      setParcelLocker(point.name);
      setShippingData(prev => ({
        ...prev,
        parcelLocker: point.name,
        street: `${point.address.street} ${point.address.building_number}, ${point.address.post_code} ${point.address.city}`
      }));
    };

    return (
      <div className="parcel-locker-section">
        <h3>Wybierz paczkomat</h3>
        {INPOST_GEOWIDGET_TOKEN && INPOST_GEOWIDGET_TOKEN !== 'YOUR_PUBLIC_INPOST_GEOWIDGET_TOKEN' && (
          <inpost-geowidget
            token={INPOST_GEOWIDGET_TOKEN}
            language="pl"
            config="parcelcollect"
            onpoint={handlePointSelect}
            style={{width: '100%', height: '500px'}}
          ></inpost-geowidget>
        )}
        {parcelLocker && (
          <div className="selected-locker-info" style={{ 
            marginTop: '15px', 
            padding: '15px', 
            backgroundColor: '#e8f5e8', 
            border: '2px solid #4caf50', 
            borderRadius: '8px' 
          }}>
            <h4 style={{ color: '#2e7d32', margin: '0 0 10px 0' }}>Wybrany paczkomat:</h4>
            <p style={{ margin: '5px 0' }}><strong>{parcelLocker}</strong></p>
            <p style={{ margin: '5px 0' }}>{shippingData.street}</p>
            <button 
              onClick={() => {
                setParcelLocker('');
                setShippingData(prev => ({ ...prev, parcelLocker: '', street: '' }));
              }}
              style={{ 
                marginTop: '10px', 
                background: '#f44336', 
                color: 'white', 
                border: 'none', 
                padding: '5px 10px', 
                borderRadius: '4px', 
                cursor: 'pointer' 
              }}
            >
              Zmień paczkomat
            </button>
          </div>
        )}
      </div>
    );
  };

  const updateUserData = async (updatedData) => {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    
    if (!userId || !token) {
      navigate('/login');
      return false;
    }

    try {
      const payload = {
        name: updatedData.firstName,
        surname: updatedData.lastName,
        phoneNumber: updatedData.phone,
        address: {
          street: updatedData.street,
          city: updatedData.city,
          postalCode: updatedData.postalCode,
          country: 'Poland'
        }
      };

      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update user data');
      }
      
      const data = await response.json();
      setUserData(data);
      return true;
    } catch (error) {
      console.error('Error updating user data:', error);
      return false;
    }
  };

  const handleQuantityChange = async (productId, change) => {
    if (isUpdatingQuantity) return;

    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');

    const item = cart.items.find(item =>
      item.productId && item.productId._id === productId
    );

    if (!item) {
      console.error('Product not found in cart');
      triggerNotificationModal('Nie znaleziono produktu w koszyku', 'error', true);
      return;
    }

    let newQuantity = item.quantity + change;

    if (newQuantity <= 0) {
      await handleRemoveItem(productId);
      return;
    }

    if (change > 0 && newQuantity > (item.productId.stock || 0)) {
      triggerNotificationModal(`Maksymalna dostępna ilość to: ${item.productId.stock || 0}`, 'warning', true);
      return;
    }

    if (quantityUpdateTimeoutRef.current) {
      clearTimeout(quantityUpdateTimeoutRef.current);
    }

    setIsUpdatingQuantity(true);

    quantityUpdateTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cart/${userId}/${productId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            quantity: newQuantity,
            action: change > 0 ? 'increase' : 'decrease'
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update quantity');
        }

        const updatedCart = await response.json();
        setCart(updatedCart);
        window.dispatchEvent(new CustomEvent('cartUpdate', { detail: updatedCart }));
        triggerNotificationModal(newQuantity === 0 ? 'Produkt usunięty z koszyka' : (change > 0 ? 'Zwiększono ilość' : 'Zmniejszono ilość'), 'success', true);
      } catch (err) {
        console.error('Error updating quantity:', err);
        triggerNotificationModal(err.message || 'Nie udało się zaktualizować ilości', 'error', true);
      } finally {
        setIsUpdatingQuantity(false);
      }
    }, 300);
  };

  const handleRemoveItem = async (productId) => {
    const item = cart.items.find(item =>
      item.productId && item.productId._id === productId
    );

    if (!item) {
      triggerNotificationModal('Nie znaleziono produktu w koszyku do usunięcia', 'error', true);
      return;
    }

    setModalMessage(`Czy na pewno chcesz usunąć "${item.productId.name}" z koszyka?`);
    setModalConfirmAction(() => async () => {
      setShowConfirmModal(false);
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/cart/${userId}/${productId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to remove item');
        }

        const updatedCart = await response.json();
        setCart(updatedCart);
        window.dispatchEvent(new CustomEvent('cartUpdate', { detail: updatedCart }));
        triggerNotificationModal('Usunięto produkt z koszyka', 'success', true);

        if (updatedCart.items.length === 0) {
          navigate('/');
        }
      } catch (err) {
        console.error('Error removing item:', err);
        triggerNotificationModal(err.message || 'Nie udało się usunąć produktu', 'error', true);
      }
    });
    setModalCancelAction(() => () => {
      setShowConfirmModal(false);
    });
    setShowConfirmModal(true);
  };
  
    const calculateSubtotal = () => {
    return cart?.items.reduce((sum, item) => sum + (item.productId.price * item.quantity), 0) || 0;
  };

  const calculateDeliveryPrice = () => {
    return deliveryMethod ? DELIVERY_METHODS[deliveryMethod].price : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateDeliveryPrice();
  };

  const validateForm = () => {
    const errors = {};
    if (!shippingData.firstName.trim()) errors.firstName = 'Imię jest wymagane';
    if (!shippingData.lastName.trim()) errors.lastName = 'Nazwisko jest wymagane';
    if (!shippingData.email.trim()) {
      errors.email = 'Email jest wymagany';
    } else if (!/\S+@\S+\.\S+/.test(shippingData.email)) {
      errors.email = 'Nieprawidłowy format email';
    }
    if (!shippingData.phone.trim()) {
      errors.phone = 'Telefon jest wymagany';
    } else if (!/^\d{9}$/.test(shippingData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Nieprawidłowy format telefonu';
    }
    if (!shippingData.postalCode.trim()) {
      errors.postalCode = 'Kod pocztowy jest wymagany';
    } else if (!/^\d{2}-\d{3}$/.test(shippingData.postalCode)) {
      errors.postalCode = 'Nieprawidłowy format kodu pocztowego (XX-XXX)';
    }
    if (!deliveryMethod) errors.deliveryMethod = 'Wybierz metodę dostawy';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    if (deliveryMethod === 'INPOST' && !parcelLocker) {
      setValidationErrors({ parcelLocker: 'Wybierz paczkomat' });
      return;
    }

    if (document.getElementById('updateProfile')?.checked) {
      const updated = await updateUserData(shippingData);
      if (updated) {
        console.log('Profil użytkownika zaktualizowany');
      }
    }
    
    setStep(3);
  };

  const handleParcelLockerSearch = async (query) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    
    if (query.length < 3) {
      setParcelLockers([]);
      return;
    }

    console.log('Searching for parcel lockers:', query);
    setSearchTimeout(setTimeout(async () => {
      try {
        const results = await searchParcelLockers(query);
        console.log('Search results:', results);
        setParcelLockers(results);
      } catch (error) {
        console.error('Error searching parcel lockers:', error);
        setParcelLockers([]);
      }
    }, 500));
  };
  
  const handleParcelLockerSelect = (locker) => {
    setParcelLocker(locker.id);
    setShippingData(prev => ({
      ...prev,
      parcelLocker: locker.id,
      street: locker.address
    }));
    setParcelLockers([]);
  };

  const initializePayment = async (orderId, paymentMethod, totalAmount) => {
    // TODO: Implement payment initialization logic
    console.log('Initializing payment for order:', orderId, paymentMethod, totalAmount);
    return { redirectUrl: '' };
  };

  const handlePayment = async () => {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    
    let csrfToken = '';
    try {
      const csrfResponse = await fetch(`${API_BASE_URL}/api/csrf-token`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.csrfToken;
      }
    } catch (err) {
      console.warn('CSRF token fetch failed, proceeding without CSRF protection:', err);
    }
    
    try {
      const orderResponse = await fetch(`${API_BASE_URL}/api/cart/${userId}/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        credentials: 'include',
        body: JSON.stringify({
          shippingData: {
            ...shippingData,
            firstName: shippingData.firstName.trim(),
            lastName: shippingData.lastName.trim(),
            email: shippingData.email.trim().toLowerCase(),
            phone: shippingData.phone.replace(/\s/g, ''),
            street: shippingData.street.trim(),
            city: shippingData.city.trim(),
            postalCode: shippingData.postalCode.trim(),
            totalAmount: calculateTotal().toFixed(2)
          },
          paymentMethod: selectedPayment,
          timestamp: new Date().toISOString()
        })
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.message || 'Błąd przetwarzania zamówienia');
      }

      const orderData = await orderResponse.json();

      const paymentData = await initializePayment(
        orderData.orderId,
        selectedPayment,
        calculateTotal()
      );

      window.dispatchEvent(new CustomEvent('cartUpdate', { detail: { items: [] } }));
      
      if (paymentData.redirectUrl) {
        window.location.href = paymentData.redirectUrl;
      } else {
        navigate(`/order-confirmation/${orderData.orderId}`);
      }
    } catch (err) {
      console.error('Error processing order:', err);
      setError(err.message || 'Wystąpił błąd podczas przetwarzania zamówienia');
    }
  };

  const handleUseRegisteredAddressToggle = () => {
    if (!useRegisteredAddress && userData) {
      setShippingData({
        ...shippingData,
        firstName: userData.name || '',
        lastName: userData.surname || '',
        street: userData.address?.street || '',
        city: userData.address?.city || '',
        postalCode: userData.address?.postalCode || '',
        phone: userData.phoneNumber || '',
      });
    }
    setUseRegisteredAddress(!useRegisteredAddress);
  };
  
    const handleDeliveryMethodSelect = (method) => {
    setDeliveryMethod(method);
    setShippingData(prev => ({ ...prev, deliveryMethod: method }));
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPayment(method);
  };


  const OrderSummary = () => (
    <div className="order-summary">
      <h3>Podsumowanie zamówienia</h3>
      <div className="order-summary-item">
        <span>Wartość produktów:</span>
        <span>{calculateSubtotal().toFixed(2)} zł</span>
      </div>
      <div className="order-summary-item">
        <span>Koszt dostawy:</span>
        <span>{calculateDeliveryPrice().toFixed(2)} zł</span>
      </div>
      <div className="order-summary-total">
        <span>Razem:</span>
        <span>{calculateTotal().toFixed(2)} zł</span>
      </div>
      
      {step === 1 && (
        <button className="next-step" onClick={() => setStep(2)}>
          Przejdź do dostawy
        </button>
      )}
      
      {step === 3 && (
        <button 
          onClick={handlePayment} 
          className="pay-button"
          disabled={!selectedPayment}
        >
          Zapłać {calculateTotal().toFixed(2)} zł
        </button>
      )}
    </div>
  );

  if (isLoading) {
    return <div className="loading">Ładowanie...</div>;
  }

  if (error) {
    return (
      <div className="checkout-container">
        <div className="error-container">
          <h2>{error}</h2>
          <button className="next-step" onClick={() => navigate('/')}>
            Wróć do sklepu
          </button>
        </div>
      </div>
    );
  }

  if (!cart || !cart.items) {
    return <div className="loading-container">Ładowanie...</div>;
  }
    return (
    <div className="checkout-container">
      {showToast && (
        <div className="toast-notification">
          {toastMessage}
        </div>
      )}
      
      <div className="checkout-steps">
        <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <span>Koszyk</span>
        </div>
        <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <span>Dostawa</span>
        </div>
        <div className={`step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <span>Płatność</span>
        </div>
      </div>

      {step === 1 && (
        <div className="checkout-content">
          <div className="form-column cart-review">
            <h2>Twój koszyk</h2>
            {cart.items.length === 0 ? (
              <p className="empty-cart-message">Twój koszyk jest pusty</p>
            ) : (
              <>
                {cart.items.some(item => !item.productId) && (
                  <div className="cart-warning">
                    <p>Niektóre produkty zostały usunięte ze sklepu i zostały automatycznie usunięte z koszyka.</p>
                  </div>
                )}
                
                {cart.items
                  .filter(item => item.productId && item.productId._id)
                  .map((item, itemIndex) => (
                    <div key={item.productId._id} className="cart-item">
                      <Link to={`/product/${item.productId.slug || ''}`} className="product-link">
                        <img 
                          src={item.productId.imageUrls?.[0] || '/placeholder.png'} 
                          alt={item.productId.name || 'Product'} 
                          onError={(e) => e.target.src = '/placeholder.png'}
                        />
                      </Link>
                      <div className="item-details">
                        <Link to={`/product/${item.productId.slug || ''}`} className="product-link">
                          <h3>{item.productId.name}</h3>
                        </Link>
                        <p>Ilość: {item.quantity}</p>
                        <p className="price">{item.productId.price} zł</p>
                        {item.quantity > (item.productId.stock || 0) && (
                          <p className="stock-warning">
                            Dostępnych tylko: {item.productId.stock} szt.
                          </p>
                        )}
                      </div>
                      <div className="item-actions">
                        <div className="quantity-control">
                          <button onClick={() => handleQuantityChange(item.productId._id, -1)}>-</button>
                          <span className="current-quantity">{item.quantity}</span>
                          <button 
                            onClick={() => handleQuantityChange(item.productId._id, 1)}
                            disabled={item.quantity >= (item.productId.stock || 0)}
                          >+</button>
                        </div>
                        <button onClick={() => handleRemoveItem(item.productId._id)} className="remove-item-btn">Usuń</button>
                      </div>
                    </div>
                  ))
                }
              </>
            )}
          </div>
          <div className="summary-column">
            <OrderSummary />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="checkout-content">
          <div className="form-column shipping-form">
            <h2>Dane do wysyłki</h2>
            {userData && (
              <div className="use-registered-address">
                <input
                  type="checkbox"
                  id="useRegisteredAddress"
                  checked={useRegisteredAddress}
                  onChange={handleUseRegisteredAddressToggle}
                />
                <label htmlFor="useRegisteredAddress">
                  Użyj adresu z mojego profilu
                </label>
              </div>
            )}
            <form onSubmit={handleShippingSubmit}>
              <div className="delivery-methods">
                <h3>Wybierz metodę dostawy:</h3>
                                <div 
                  key={'INPOST'} 
                  className={`delivery-option ${deliveryMethod === 'INPOST' ? 'selected' : ''}`}
                  onClick={() => handleDeliveryMethodSelect('INPOST')}
                >
                  <input
                    type="radio"
                    id={'INPOST'}
                    name="delivery"
                    value={'INPOST'}
                    checked={deliveryMethod === 'INPOST'}
                    onChange={() => handleDeliveryMethodSelect('INPOST')}
                  />
                  <label htmlFor={'INPOST'}>{DELIVERY_METHODS.INPOST.name} - {DELIVERY_METHODS.INPOST.price} zł</label>
                </div>
                {validationErrors.deliveryMethod && (
                  <div className="error-message">{validationErrors.deliveryMethod}</div>
                )}
              </div>

              {deliveryMethod === 'INPOST' && renderParcelLockerSection()}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Imię</label>
                  <input
                    id="firstName"
                    type="text"
                    placeholder="Imię"
                    value={shippingData.firstName}
                    onChange={e => setShippingData({...shippingData, firstName: e.target.value})}
                    className={validationErrors.firstName ? 'error' : ''}
                    disabled={useRegisteredAddress && userData?.name}
                  />
                  {validationErrors.firstName && <span className="error-message">{validationErrors.firstName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Nazwisko</label>
                  <input
                    id="lastName"
                    type="text"
                    placeholder="Nazwisko"
                    value={shippingData.lastName}
                    onChange={e => setShippingData({...shippingData, lastName: e.target.value})}
                    className={validationErrors.lastName ? 'error' : ''}
                    disabled={useRegisteredAddress && userData?.surname}
                  />
                  {validationErrors.lastName && <span className="error-message">{validationErrors.lastName}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="Email"
                  value={shippingData.email}
                  onChange={e => setShippingData({...shippingData, email: e.target.value})}
                  className={validationErrors.email ? 'error' : ''}
                  disabled={useRegisteredAddress && userData?.email}
                />
                {validationErrors.email && <span className="error-message">{validationErrors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Telefon</label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="Telefon"
                  value={shippingData.phone}
                  onChange={e => setShippingData({...shippingData, phone: e.target.value})}
                  className={validationErrors.phone ? 'error' : ''}
                  disabled={useRegisteredAddress && userData?.phoneNumber}
                />
                {validationErrors.phone && <span className="error-message">{validationErrors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="street">Ulica i numer</label>
                <input
                  id="street"
                  type="text"
                  placeholder="Ulica i numer"
                  value={shippingData.street}
                  onChange={e => setShippingData({...shippingData, street: e.target.value})}
                  className={validationErrors.street ? 'error' : ''}
                  disabled={useRegisteredAddress && userData?.address?.street && deliveryMethod !== 'INPOST'}
                />
                {validationErrors.street && <span className="error-message">{validationErrors.street}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="postalCode">Kod pocztowy</label>
                  <input
                    id="postalCode"
                    type="text"
                    placeholder="XX-XXX"
                    value={shippingData.postalCode}
                    onChange={e => setShippingData({...shippingData, postalCode: e.target.value})}
                    className={validationErrors.postalCode ? 'error' : ''}
                    disabled={useRegisteredAddress && userData?.address?.postalCode && deliveryMethod !== 'INPOST'}
                  />
                  {validationErrors.postalCode && <span className="error-message">{validationErrors.postalCode}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="city">Miasto</label>
                  <input
                    id="city"
                    type="text"
                    placeholder="Miasto"
                    value={shippingData.city}
                    onChange={e => setShippingData({...shippingData, city: e.target.value})}
                    className={validationErrors.city ? 'error' : ''}
                    disabled={useRegisteredAddress && userData?.address?.city && deliveryMethod !== 'INPOST'}
                  />
                  {validationErrors.city && <span className="error-message">{validationErrors.city}</span>}
                </div>
              </div>

              <div className="update-profile-option">
                <input
                  type="checkbox"
                  id="updateProfile"
                  defaultChecked={false}
                />
                <label htmlFor="updateProfile">
                  Zaktualizuj mój profil tymi danymi
                </label>
              </div>

              <div className="form-buttons">
                <button type="button" className="back-step" onClick={() => setStep(1)}>
                  Wróć
                </button>
                <button type="submit" className="next-step">
                  Dalej do płatności
                </button>
              </div>
            </form>
          </div>
          <div className="summary-column">
            <OrderSummary />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="checkout-content">
          <div className="form-column payment-section">
            <h2>Płatność</h2>
            <div className="payment-methods">
              <h3>Wybierz metodę płatności:</h3>
              {PAYMENT_METHODS.map(method => (
                <div 
                  key={method.id} 
                  className={`payment-option ${selectedPayment === method.id ? 'selected' : ''}`}
                  onClick={() => handlePaymentMethodSelect(method.id)}
                >
                  <input
                    type="radio"
                    id={method.id}
                    name="payment"
                    value={method.id}
                    checked={selectedPayment === method.id}
                    onChange={() => handlePaymentMethodSelect(method.id)}
                    className={validationErrors.payment ? 'error' : ''}
                  />
                  <label htmlFor={method.id}>{method.name}</label>
                </div>
              ))}
              {validationErrors.payment && <div className="error-message">{validationErrors.payment}</div>}
            </div>
            <button type="button" className="back-step" onClick={() => setStep(2)}>
              Wróć
            </button>
          </div>
          <div className="summary-column">
            <OrderSummary />
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmModal}
        message={modalMessage}
        onConfirm={modalConfirmAction}
        onCancel={modalCancelAction}
        onClose={() => setShowConfirmModal(false)}
      />

      <ConfirmationModal
        isOpen={showNotificationModal}
        message={notificationMessage}
        type={notificationType}
        autoDismiss={notificationAutoDismiss}
        onClose={() => setShowNotificationModal(false)}
      />
    </div>
  );
}
